import React, { useEffect, useState } from 'react';
import { View, ScrollView } from 'react-native';
import BypassCard from '../../../../components/Cards/BypassCard'; // Adjust the import path as needed
import cardTruck from 'src/assets/cardTruck-4x.png';
import Button from 'src/components/Button/Button';
import plusIcon from 'src/assets/plux-3x.png';
import TourWrapper from 'src/components/TourWrapper/TourWrapper';
import { useTourAutoStart } from 'src/features/tour/hooks/useTourAutoStart';
import { getVehicleBypassTour } from 'src/features/tour/tourSteps';
import { useLinkTo } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveTab, setFilledTabsIndex } from 'src/slices/UiSlice';
import {
  useDeleteVehicleByIdMutation,
  useGetAllVehiclesQuery,
} from 'src/features/vehicles/api/vehicleApi';
import {
  setAllVehicles,
  setCurrentVehicle,
  toggleVehicleToast,
} from 'src/features/vehicles/slice/vehicleSlice';
import { RootState } from 'src/store/store';
import { URLS } from 'src/constants/urls';
import Toast from 'src/components/Toast/Toast';
import { styles } from './VehicleBypass.styles';
import { TABS_INDEX } from 'src/constants/tabs';
import { toggleToastState } from 'src/helpers/toastHelper';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents';

const VehicleBypass: React.FC = () => {
  const dispatch = useDispatch();
  const linkTo = useLinkTo();

  const vehicles = useSelector((state: RootState) => state.vehicles.vehicles);
  const drivers = useSelector((state: RootState) => state.drivers.drivers);
  const showVehicleToast = useSelector((state: RootState) => state.vehicles.showVehicleToast);
  const currentCompany = useSelector((state: RootState) => state.company.company);
  const accountType = useSelector((state: RootState) => state.UI.accountType);

  const [deleteVehicle] = useDeleteVehicleByIdMutation();
  const [showDeleteSuccessToast, setShowDeleteSuccessToast] = useState(false);
  const [showDeleteFailToast, setShowDeleteFailToast] = useState(false);
  //pass company id
  const {
    data: updatedVehicles,
    isSuccess,
    refetch,
  } = useGetAllVehiclesQuery(currentCompany?.id || 0); //BE endpoint needed

  //const { data: updatedVehicles, isSuccess, refetch } = useGetAllVehiclesQuery();
  useEffect(() => {
    if (isSuccess) {
      dispatch(setAllVehicles(updatedVehicles));
      dispatch(setCurrentVehicle({}));
    }
  }, []);

  useEffect(() => {
    if (showVehicleToast) {
      refetch();
    }
  }, [showVehicleToast]);

  useEffect(() => {
    if (showVehicleToast) {
      const timeout = setTimeout(() => {
        dispatch(toggleVehicleToast(false));
      }, 3000);
      return () => clearTimeout(timeout); // Cleanup timeout
    }
  }, [showVehicleToast, dispatch]);

  // Track when the user lands on the Vehicles Summary page
  useEffect(() => {
    trackGTMEvent(
      'vehicles-summary-landed',
      {
        userEvent: 'User landed on vehicle summary screen',
      },
      accountType,
    );
  }, []);

  // Auto-start tour on first visit
  useTourAutoStart({
    tourKey: 'vehicleBypass',
    driverSteps: getVehicleBypassTour(),
    enabled: isSuccess && updatedVehicles !== undefined,
  });

  const handleDelete = async (vehicleId: number) => {
    try {
      await deleteVehicle(vehicleId).unwrap();
      setShowDeleteSuccessToast(true);
      toggleToastState(setShowDeleteSuccessToast);

      // Track Vehicle Deleted event
      trackGTMEvent(
        'vehicle-deleted',
        {
          userEvent: 'User pressed delete vehicle button on vehicle card',
          vehicleId,
        },
        accountType,
      );
    } catch (error) {
      setShowDeleteFailToast(true);
      toggleToastState(setShowDeleteFailToast);
      console.error('Failed to delete vehicle:', error);
    }
  };

  const handleEdit = (vehicleId: number, index: number) => {
    // Track Vehicle Edited event
    trackGTMEvent(
      'vehicle-edited',
      {
        userEvent: 'User pressed edit vehicle button on vehicle card',
        vehicleId,
      },
      accountType,
    );

    dispatch(setCurrentVehicle(vehicles[index]));
    linkTo(`${URLS.VEHICLE.START}/${vehicleId}`);
  };

  const handleBack = () => {
    trackGTMEvent(
      'vehicles-summary-left',
      {
        buttonTitle: 'Back',
      },
      accountType,
    );

    linkTo(URLS.COMPANY.LANDING);
    dispatch(setActiveTab({ activeTab: TABS_INDEX.COMPANY }));
  };

  const handleNext = () => {
    trackGTMEvent(
      'vehicles-summary-next',
      {
        buttonTitle: 'Next',
      },
      accountType,
    );

    dispatch(setActiveTab({ activeTab: TABS_INDEX.DRIVER }));
    dispatch(setFilledTabsIndex({ filledTabsIndex: TABS_INDEX.DRIVER }));

    if (drivers.length > 0) {
      linkTo(URLS.DRIVER.BYPASS);
    } else {
      linkTo(URLS.DRIVER.LICENSE);
    }
  };

  const handleAddAnotherVehicle = () => {
    // Track Another Vehicle Added event
    trackGTMEvent(
      'add-vehicle-button-presses',
      {
        screenName: 'VehiclesSummaryPage',
        buttonTitle: 'Add Vehicle',
        companyId: currentCompany?.id,
      },
      accountType,
    );

    linkTo(URLS.VEHICLE.START);
  };

  return (
    <ScrollView
      contentContainerStyle={styles.container}
      keyboardShouldPersistTaps="handled"
      bounces={false}>
      <View style={styles.cardContainer}>
        <TourWrapper tourId="vehicle-list">
          <View>
            {vehicles.length > 0 &&
              vehicles.map((vehicle, index) => (
                <TourWrapper key={index} tourId={index === 0 ? 'vehicle-card-actions' : undefined}>
                  <BypassCard
                    testID={`vehicle-bypass-item-card-${index}`}
                    imageSrc={cardTruck}
                    imageWidth={67.2}
                    imageHeight={32}
                    title={vehicle.year + ' ' + vehicle.make + ' ' + vehicle.model}
                    description={vehicle.vin as string}
                    onEdit={() => {
                      handleEdit(vehicle.id as number, index);
                    }} // Define edit functionality
                    onDelete={() => handleDelete(vehicle.id as number)} // Define delete functionality
                  />
                </TourWrapper>
              ))}
          </View>
        </TourWrapper>
        <TourWrapper tourId="add-vehicle-btn">
          <Button
            testID={'vehicle-bypass-add-another'}
            title="Add another vehicle"
            type="tertiary"
            colorVariant="default"
            icon={plusIcon}
            iconWidth={17}
            iconHeight={17}
            onPress={handleAddAnotherVehicle}
          />
        </TourWrapper>
        <View style={styles.bottomContainer}>
          {showVehicleToast && (
            <Toast text="Details for this vehicle have been added successfully!" animate={true} />
          )}
          {showDeleteSuccessToast && (
            <Toast text="The selected vehicle has been deleted." animate={true} />
          )}
          {showDeleteFailToast && (
            <Toast
              text="An error occurred while deleting the selected vehicle. Please try again later."
              animate={true}
            />
          )}
        </View>
      </View>

      <View style={styles.buttonContainer}>
        <View style={styles.buttonWrapper1}>
          <Button
            type="tertiary"
            title="Back"
            onPress={handleBack}
            testID={'vehicle-bypass-back-btn'}
          />
        </View>
        <View style={styles.buttonWrapper2}>
          <Button
            testID={'vehicle-bypass-next-btn'}
            type="primary"
            colorVariant={vehicles.length === 0 ? 'disabled' : 'default'}
            title="Next"
            onPress={handleNext}
          />
        </View>
      </View>
    </ScrollView>
  );
};

export default VehicleBypass;
